import React, { useCallback, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_TIMEOUT_MS = 3000;   // L9-1: a slow or hung /mode never blocks a connect (rule 15)

// The D-mode warning before a Planning Center connect (Section 6 Phase 5 step 3,
// M-01): the connect entry point calls `intercept(proceed)` first. It reads
// GET /api/services/mode/{userId}; only when the church is in `simple` mode with
// a template does it hold the connect and show the dialog. Every other answer
// (flag off, pco, no template, a failed request, or no answer within 3 s) returns
// false and the caller connects exactly as before. `dialog` is null until the warning first
// opens: a mounted-but-closed Radix dialog still takes three React useId values,
// which would shift every later `radix-…` id on the page for a flag-off user.
export function useServicesConnectGuard(userId) {
  const [open, setOpen] = useState(false);
  const [used, setUsed] = useState(false);   // mounted only once the warning has been needed
  const proceedRef = useRef(null);
  const passRef = useRef(false);

  const intercept = useCallback(async (proceed) => {
    if (passRef.current) {        // the confirmed call coming back through the guard
      passRef.current = false;
      return false;
    }
    if (!userId) return false;
    let data = null;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MODE_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_URL}/api/services/mode/${encodeURIComponent(userId)}`, { signal: ctrl.signal });
      if (res.ok) data = await res.json();
    } catch { data = null; }   // timeout or error: fail open, connect exactly as before
    finally { clearTimeout(timer); }
    if (data?.mode === 'simple' && data?.has_template) {
      proceedRef.current = proceed;
      setUsed(true);
      setOpen(true);
      return true;
    }
    return false;
  }, [userId]);

  const onContinue = () => {
    const proceed = proceedRef.current;
    proceedRef.current = null;
    setOpen(false);
    if (proceed) {
      passRef.current = true;
      proceed();
    }
  };

  const dialog = !used ? null : (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); proceedRef.current = null; } }}>
      <AlertDialogContent data-testid="services-pco-connect-warning">
        <AlertDialogHeader>
          <AlertDialogTitle>Connect Planning Center?</AlertDialogTitle>
          <AlertDialogDescription>
            You have Simple Planner services. They'll be hidden while Planning Center is connected. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="services-pco-connect-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-teal-600 hover:bg-teal-700 text-white"
            onClick={onContinue}
            data-testid="services-pco-connect-continue"
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { intercept, dialog };
}

export default useServicesConnectGuard;
